/**
 * AppSelector.tsx - Entry point for the main application
 * 
 * Handles subdomain detection and routing:
 * - If subdomain detected: redirects to whitelabel app
 * - If no subdomain: renders the main ZuriLink app
 * 
 * Note: The main app uses RouterProvider (React Router v6.4+) internally,
 * so we don't need BrowserRouter here.
 */
import React, { Suspense, useEffect, useState } from "react";
import RootStory from "./pages/RootStory";
import { getSubdomain, startsOnPublicStory } from "./utils/appEntry";

// Router, Firebase Auth, and the member provider tree are unnecessary for the public `/`.
// Keep them behind the state-aware boundary so a first visit can paint from one small graph.
const MainApp = React.lazy(() => import("./App"));

const AppSelector: React.FC = () => {
  // Hostname is synchronously available. The previous state/effect pair guaranteed a
  // one-frame full-screen spinner before every route, including the static public story.
  const subdomain = getSubdomain(window.location.hostname);
  // Keep hydration consistent if another tab changes storage while scripts download.
  // The existing Firebase reconciliation still restores the actual session afterward.
  const [startsOnPublicRoot] = useState(() =>
    document.getElementById("root")?.dataset.prerendered === "story" || startsOnPublicStory()
  );
  const [showMainApp, setShowMainApp] = useState(!startsOnPublicRoot);

  useEffect(() => {
    if (!startsOnPublicRoot) return;

    // A missing marker normally means a stranger, but IndexedDB and localStorage can be
    // cleared independently. Reconcile that rare state after the story has painted; only a
    // real restored Firebase session is allowed to replace the public entry with the app.
    let cancelled = false;
    let unsubscribe: (() => void) | undefined;
    const timer = window.setTimeout(() => {
      void Promise.all([import("./firebaseAuth"), import("firebase/auth")])
        .then(([{ auth }, { onAuthStateChanged }]) => {
          if (cancelled) return;
          unsubscribe = onAuthStateChanged(auth, (user) => {
            if (user && !cancelled) setShowMainApp(true);
            unsubscribe?.();
          });
        })
        .catch((error: unknown) => {
          // The public story remains fully usable offline or when auth cannot initialize.
          console.warn("Deferred session reconciliation unavailable", error);
        });
    }, 1200);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      unsubscribe?.();
    };
  }, [startsOnPublicRoot]);
  const appWindow = window as typeof window & {
    __ZURILINK_SUBDOMAIN__?: string;
    __ZURILINK_APP_MODE__?: "main" | "whitelabel";
  };
  if (subdomain) {
    appWindow.__ZURILINK_SUBDOMAIN__ = subdomain;
    appWindow.__ZURILINK_APP_MODE__ = "whitelabel";
  } else {
    appWindow.__ZURILINK_APP_MODE__ = "main";
  }

  // In development the whitelabel app runs as a separate dev server, so hand off to it.
  if (subdomain && window.location.hostname.includes("localhost")) {
    const whitelabelUrl = `http://localhost:3001${window.location.pathname}${window.location.search}${window.location.hash}`;
    window.location.href = whitelabelUrl;
    return null;
  }

  // In PRODUCTION there is deliberately no redirect. A real tenant's domain is attached
  // directly to the whitelabel hosting target, which serves the whitelabel bundle — this
  // component never runs for them. So reaching here means a subdomain is pointed at the MAIN
  // site, i.e. a hosting misconfiguration, and no client-side redirect can repair that.
  //
  // The previous code sent the browser to `https://<sub>.zurilink-whitelabel.web.app`, a host
  // that does not exist, so visitors got a full-page NET::ERR_CERT_COMMON_NAME_INVALID
  // warning. Rendering the main app is a strictly better failure mode: the visitor gets a
  // working product instead of a security alert, and the misrouted domain shows up in
  // analytics rather than silently scaring people away.
  if (subdomain) {
    console.warn(
      `[AppSelector] "${subdomain}.zurilink.com" is served by the MAIN hosting target. ` +
        `Point it at the whitelabel target, or remove the custom domain. Rendering main app.`
    );
  }

  if (!showMainApp) {
    return <RootStory />;
  }

  // MainApp uses RouterProvider internally, so no BrowserRouter is needed here.
  return (
    <Suspense
      fallback={
        <div className="flex min-h-screen items-center justify-center bg-background px-6 text-center text-sm text-muted-foreground">
          Loading ZuriLink…
        </div>
      }
    >
      <MainApp />
    </Suspense>
  );
};

export default AppSelector;
